import { useState } from "react";
import { Link } from "react-router-dom";
import { ChevronDown, ArrowUpRight } from "lucide-react";
import api, { formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";

const BOOKING_CONDITIONS = [
    "A non refundable deposit of 50% of the total hire fee hire is required at the time of booking to secure your date. The remaining balance is due 3 days prior to delivery.",
    "Delivery/pick up is an additional charge according to the location, which will be quoted when filling the necessary details on the enquiry form.",
    "A refundable bond $350 is payable in addition to the hire fee. This bond will be refunded in full within 3 business days of the cakes return, provided it is returned its original condition.",
    "The standard rental period is 2-3 days from the date of delivery. Extended hire periods may be arranged by prior agreement and may incur additional charges.",
];

export default function HireInquiryForm({ cake }) {
    const [form, setForm] = useState({
        event_date: "",
        venue: "",
        name: "",
        phone: "",
        message: "",
        flavour_choice: "",
    });
    const [wantsSlab, setWantsSlab] = useState(false);
    const [slabGuests, setSlabGuests] = useState("");
    const [slabServes, setSlabServes] = useState("");
    const [slabType, setSlabType] = useState("");
    const [conditionsOpen, setConditionsOpen] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    const onChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

    const onSubmit = async (e) => {
        e.preventDefault();
        if (submitting) return;
        setSubmitting(true);
        try {
            await api.post("/inquiries", {
                ...form,
                cake_slug: cake?.slug,
                cake_name: cake?.name,
                cake_slab_guests: wantsSlab ? slabGuests : null,
                cake_slab_serves: wantsSlab ? slabServes : null,
                cake_slab_type: wantsSlab ? slabType : null,
            });
            toast.success("Thank you! Your enquiry has been sent. We'll be in touch shortly.");
            setForm({ event_date: "", venue: "", name: "", phone: "", message: "", flavour_choice: "" });
            setWantsSlab(false);
            setSlabGuests("");
            setSlabServes("");
            setSlabType("");
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail));
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <form onSubmit={onSubmit} className="space-y-5" data-testid="hire-inquiry-form">
            <div>
                <label className="block text-[10px] tracking-[0.25em] uppercase text-ink-soft mb-2">Date *</label>
                <input
                    type="date"
                    name="event_date"
                    required
                    value={form.event_date}
                    onChange={onChange}
                    className="w-full px-4 py-3 bg-transparent border border-[#EAE3DE] focus:outline-none focus:border-blush text-ink"
                    data-testid="hire-date"
                />
            </div>

            <div>
                <label className="block text-[10px] tracking-[0.25em] uppercase text-ink-soft mb-2">Delivery Venue *</label>
                <input
                    type="text"
                    name="venue"
                    required
                    value={form.venue}
                    onChange={onChange}
                    className="w-full px-4 py-3 bg-transparent border border-[#EAE3DE] focus:outline-none focus:border-blush text-ink"
                    data-testid="hire-venue"
                />
                <p className="text-[11px] text-ink-soft italic mt-1.5">Delivery/pickup is an additional cost, quoted based on your location.</p>
            </div>

            <div className="pt-2">
                <p className="text-[10px] tracking-[0.3em] uppercase text-blush font-medium mb-3">Contact details</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                        <label className="block text-[10px] tracking-[0.25em] uppercase text-ink-soft mb-2">Name *</label>
                        <input
                            type="text"
                            name="name"
                            required
                            value={form.name}
                            onChange={onChange}
                            className="w-full px-4 py-3 bg-transparent border border-[#EAE3DE] focus:outline-none focus:border-blush text-ink"
                            data-testid="hire-name"
                        />
                    </div>
                    <div>
                        <label className="block text-[10px] tracking-[0.25em] uppercase text-ink-soft mb-2">Mobile Number *</label>
                        <input
                            type="tel"
                            name="phone"
                            required
                            value={form.phone}
                            onChange={onChange}
                            className="w-full px-4 py-3 bg-transparent border border-[#EAE3DE] focus:outline-none focus:border-blush text-ink"
                            data-testid="hire-phone"
                        />
                    </div>
                </div>
                <div className="mt-4">
                    <label className="block text-[10px] tracking-[0.25em] uppercase text-ink-soft mb-2">Message</label>
                    <textarea
                        name="message"
                        rows={3}
                        value={form.message}
                        onChange={onChange}
                        className="w-full px-4 py-3 bg-transparent border border-[#EAE3DE] focus:outline-none focus:border-blush text-ink resize-none"
                        data-testid="hire-message"
                    />
                </div>
            </div>

            <div className="pt-2 border-t border-[#EAE3DE]">
                <label className="flex items-center gap-2 pt-4 mb-3 cursor-pointer">
                    <input
                        type="checkbox"
                        checked={wantsSlab}
                        onChange={(e) => setWantsSlab(e.target.checked)}
                        data-testid="hire-slab-toggle"
                    />
                    <span className="text-[10px] tracking-[0.25em] uppercase text-ink-soft">Add a cake slab (optional)</span>
                </label>
                {wantsSlab && (
                    <div className="space-y-4">
                        <div>
                            <label className="block text-[10px] tracking-[0.25em] uppercase text-ink-soft mb-2">Total number of guests</label>
                            <input
                                type="number"
                                min="1"
                                value={slabGuests}
                                onChange={(e) => setSlabGuests(e.target.value)}
                                className="w-full px-4 py-3 bg-transparent border border-[#EAE3DE] focus:outline-none focus:border-blush text-ink"
                                data-testid="hire-slab-guests"
                            />
                        </div>
                        <div>
                            <label className="block text-[10px] tracking-[0.25em] uppercase text-ink-soft mb-2">Main dessert or coffee serves</label>
                            <div className="flex items-center gap-6">
                                <label className="flex items-center gap-2 cursor-pointer">
                                    <input
                                        type="checkbox"
                                        checked={slabType === "Dessert"}
                                        onChange={() => setSlabType(slabType === "Dessert" ? "" : "Dessert")}
                                        data-testid="hire-slab-type-dessert"
                                    />
                                    <span className="text-sm text-ink">Dessert</span>
                                </label>
                                <label className="flex items-center gap-2 cursor-pointer">
                                    <input
                                        type="checkbox"
                                        checked={slabType === "Coffee"}
                                        onChange={() => setSlabType(slabType === "Coffee" ? "" : "Coffee")}
                                        data-testid="hire-slab-type-coffee"
                                    />
                                    <span className="text-sm text-ink">Coffee</span>
                                </label>
                            </div>
                        </div>
                        <div>
                            <label className="block text-[10px] tracking-[0.25em] uppercase text-ink-soft mb-2">How many serves</label>
                            <input
                                type="number"
                                min="1"
                                value={slabServes}
                                onChange={(e) => setSlabServes(e.target.value)}
                                className="w-full px-4 py-3 bg-transparent border border-[#EAE3DE] focus:outline-none focus:border-blush text-ink"
                                data-testid="hire-slab-serves"
                            />
                        </div>
                    </div>
                )}
            </div>

            <div>
                <div className="flex items-center justify-between mb-2">
                    <label className="block text-[10px] tracking-[0.25em] uppercase text-ink-soft">Flavour</label>
                    <Link
                        to="/flavours"
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center gap-1 text-[10px] tracking-[0.2em] uppercase text-blush hover:text-[#C4007A] transition-colors shrink-0"
                        data-testid="hire-flavour-list-link"
                    >
                        View flavours list <ArrowUpRight size={11} />
                    </Link>
                </div>
                <input
                    type="text"
                    name="flavour_choice"
                    value={form.flavour_choice}
                    onChange={onChange}
                    placeholder="e.g. Vanilla, Chocolate…"
                    className="w-full px-4 py-3 bg-transparent border border-[#EAE3DE] focus:outline-none focus:border-blush text-ink"
                    data-testid="hire-flavour-choice"
                />
            </div>

            <div className="border-t border-[#EAE3DE] pt-4">
                <button
                    type="button"
                    onClick={() => setConditionsOpen((v) => !v)}
                    className="flex items-center justify-between w-full text-left"
                    data-testid="hire-conditions-toggle"
                >
                    <span className="text-[11px] tracking-[0.25em] uppercase text-ink font-medium">Booking and Conditions</span>
                    <span className="w-7 h-7 border border-ink flex items-center justify-center shrink-0">
                        <ChevronDown size={14} className={`transition-transform ${conditionsOpen ? "rotate-180" : ""}`} />
                    </span>
                </button>
                {conditionsOpen && (
                    <div className="mt-4 space-y-3" data-testid="hire-conditions-content">
                        {BOOKING_CONDITIONS.map((c, i) => (
                            <p key={i} className="text-xs text-ink-soft leading-relaxed">{c}</p>
                        ))}
                    </div>
                )}
            </div>

            <button
                type="submit"
                disabled={submitting}
                className="btn-pink w-full disabled:opacity-60"
                data-testid="hire-inquiry-submit"
            >
                {submitting ? "Sending…" : "Send Enquiry"}
            </button>
        </form>
    );
}
